import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Phone, MessageSquare, Mail, CheckCircle2 } from "lucide-react";
import {
    CUSTOMER_TYPES,
    SITUATIONS,
    USAGE_BUCKETS,
    CONTACT_METHODS,
    MOVE_DATES,
    BUSINESS_TYPES,
    US_STATES,
    SERVICES_OF_INTEREST,
} from "@/lib/constants";
import { submitLead, formatApiErrorDetail } from "@/lib/api";
import { useT } from "@/lib/i18n";

const DEFAULT_STATE = {
    customer_type: "",
    service_of_interest: "Electricity",
    zip_code: "",
    state: "",
    utility_territory: "",
    situation: "",
    monthly_usage: "",
    move_date: "",
    business_type: "",
    number_of_locations: "",
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    preferred_contact: "",
    consent: false,
    notes: "",
};

const stepLabels = ["You", "Location", "Situation", "Usage", "Contact", "Review"];
const STEP_KEYS = ["form_step_you", "form_step_location", "form_step_situation", "form_step_usage", "form_step_contact", "form_step_review"];

function readUtm() {
    if (typeof window === "undefined") return {};
    const p = new URLSearchParams(window.location.search);
    return {
        utm_source: p.get("utm_source") || p.get("src") || "",
        utm_medium: p.get("utm_medium") || "",
        utm_campaign: p.get("utm_campaign") || "",
    };
}

export default function LeadForm({ initial = {}, source = "website" }) {
    const navigate = useNavigate();
    const { t, lang } = useT();
    const [step, setStep] = useState(0);
    const [state, setState] = useState({ ...DEFAULT_STATE, ...initial });
    const [submitting, setSubmitting] = useState(false);

    const isBusiness = state.customer_type === "business";

    useEffect(() => {
        // auto-pick move date on moving selection
        if (state.customer_type === "moving" && !state.move_date) {
            setState((s) => ({ ...s, move_date: "Within 30 days" }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.customer_type]);

    const canNext = useMemo(() => {
        switch (step) {
            case 0: return !!state.customer_type;
            case 1: return /^\d{5}$/.test(state.zip_code) && !!state.state;
            case 2: return !!state.situation;
            case 3: return !!state.monthly_usage;
            case 4:
                return (
                    state.first_name.trim().length > 0 &&
                    state.last_name.trim().length > 0 &&
                    /^[+]?[\d\s().-]{7,}$/.test(state.phone) &&
                    /.+@.+\..+/.test(state.email) &&
                    !!state.preferred_contact
                );
            case 5: return state.consent;
            default: return false;
        }
    }, [step, state]);

    const update = (k, v) => setState((s) => ({ ...s, [k]: v }));

    const next = () => setStep((s) => Math.min(s + 1, stepLabels.length - 1));
    const prev = () => setStep((s) => Math.max(s - 1, 0));

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const utm = readUtm();
            const customerLabel = CUSTOMER_TYPES.find((c) => c.id === state.customer_type)?.label || state.customer_type;
            const payload = {
                customer_type: customerLabel,
                service_of_interest: state.service_of_interest || "Electricity",
                category: "customer",
                lang: lang || "en",
                referred_by_code: (new URLSearchParams(window.location.search).get("ref") || window.localStorage.getItem("ps_ref") || "").toUpperCase() || null,
                zip_code: state.zip_code,
                state: state.state || null,
                utility_territory: state.utility_territory || null,
                situation: state.situation,
                monthly_usage: state.monthly_usage || null,
                business_type: isBusiness ? state.business_type || null : null,
                number_of_locations: isBusiness && state.number_of_locations
                    ? Number(state.number_of_locations) : null,
                move_date: state.move_date || null,
                first_name: state.first_name.trim(),
                last_name: state.last_name.trim(),
                phone: state.phone.trim(),
                email: state.email.trim().toLowerCase(),
                preferred_contact: state.preferred_contact,
                consent: !!state.consent,
                notes: state.notes || null,
                source,
                ...utm,
            };
            const data = await submitLead(payload);
            toast.success("Request received. Check your email!");
            const q = new URLSearchParams({
                id: data.id,
                service: payload.service_of_interest || "Electricity",
                channel: payload.utm_source || payload.source || "",
                name: `${payload.first_name} ${payload.last_name}`.trim(),
                phone: payload.phone || "",
                email: payload.email || "",
            }).toString();
            navigate(`/thank-you?${q}`);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="card-surface p-6 sm:p-8" data-testid="lead-form">
            {/* Progress */}
            <div className="flex items-center gap-2 overflow-x-auto pb-1" aria-label="Progress">
                {stepLabels.map((label, i) => (
                    <div key={label} className="flex items-center gap-2 shrink-0">
                        <span
                            data-testid={`step-dot-${i}`}
                            className={`step-dot ${i === step ? "active" : i < step ? "done" : ""}`}
                        >
                            {i < step ? "✓" : i + 1}
                        </span>
                        <span className={`text-xs font-medium ${i === step ? "text-white" : "text-slate-500"}`}>
                            {t(STEP_KEYS[i])}
                        </span>
                        {i < stepLabels.length - 1 && (
                            <span className="w-6 h-px bg-white/10 mx-1" />
                        )}
                    </div>
                ))}
            </div>

            {/* Step bodies */}
            <div className="mt-7 min-h-[260px]">
                {step === 0 && (
                    <div data-testid="step-customer-type">
                        <h3 className="text-xl font-semibold text-white">Which best describes you?</h3>
                        <p className="text-sm text-slate-400 mt-1">We tailor the questions to your situation.</p>
                        <div className="mt-5 grid gap-3 sm:grid-cols-2">
                            {CUSTOMER_TYPES.map((c) => (
                                <button
                                    key={c.id}
                                    type="button"
                                    data-testid={`customer-type-${c.id}`}
                                    onClick={() => update("customer_type", c.id)}
                                    className={`option-tile text-left ${state.customer_type === c.id ? "selected" : ""}`}
                                >
                                    <div>
                                        <div className="font-semibold">{c.label}</div>
                                        <div className="text-xs text-slate-400">{c.description}</div>
                                    </div>
                                </button>
                            ))}
                        </div>
                        <div className="mt-6">
                            <label className="text-xs text-slate-400">Which service do you want help with?</label>
                            <div className="mt-2 flex flex-wrap gap-2">
                                {SERVICES_OF_INTEREST.map((svc) => (
                                    <button
                                        key={svc}
                                        type="button"
                                        data-testid={`service-${svc.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                        onClick={() => update("service_of_interest", svc)}
                                        className={`px-3 py-2 rounded-full text-xs sm:text-sm border transition ${
                                            state.service_of_interest === svc
                                                ? "bg-emerald-500/15 border-emerald-500 text-emerald-200"
                                                : "border-white/10 text-slate-300 hover:border-emerald-500/40"
                                        }`}
                                    >
                                        {svc}
                                    </button>
                                ))}
                            </div>
                            <p className="mt-2 text-xs text-slate-500">Electricity is our specialty — availability for other services varies.</p>
                        </div>
                    </div>
                )}

                {step === 1 && (
                    <div data-testid="step-location">
                        <h3 className="text-xl font-semibold text-white">Where is the service address?</h3>
                        <p className="text-sm text-slate-400 mt-1">We use ZIP to confirm what plans are available.</p>
                        <div className="mt-5 grid gap-3 sm:grid-cols-2">
                            <div>
                                <label className="text-xs text-slate-400">ZIP code</label>
                                <input
                                    data-testid="zip-input"
                                    inputMode="numeric"
                                    maxLength={5}
                                    value={state.zip_code}
                                    onChange={(e) => update("zip_code", e.target.value.replace(/[^0-9]/g, ""))}
                                    className="input-dark mt-1"
                                    placeholder="77002"
                                />
                            </div>
                            <div>
                                <label className="text-xs text-slate-400">State</label>
                                <select
                                    data-testid="state-select"
                                    value={state.state}
                                    onChange={(e) => update("state", e.target.value)}
                                    className="input-dark mt-1"
                                >
                                    <option value="">Select state</option>
                                    {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                            <div className="sm:col-span-2">
                                <label className="text-xs text-slate-400">Utility territory (if you know it)</label>
                                <input
                                    data-testid="utility-input"
                                    value={state.utility_territory}
                                    onChange={(e) => update("utility_territory", e.target.value)}
                                    className="input-dark mt-1"
                                    placeholder="e.g. CenterPoint, Oncor, AEP..."
                                />
                            </div>
                        </div>
                    </div>
                )}

                {step === 2 && (
                    <div data-testid="step-situation">
                        <h3 className="text-xl font-semibold text-white">What's going on right now?</h3>
                        <div className="mt-5 grid gap-3">
                            {SITUATIONS.map((s) => (
                                <button
                                    key={s}
                                    type="button"
                                    data-testid={`situation-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}`}
                                    onClick={() => update("situation", s)}
                                    className={`option-tile text-left ${state.situation === s ? "selected" : ""}`}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                        {state.customer_type === "moving" && (
                            <div className="mt-5">
                                <label className="text-xs text-slate-400">When are you moving?</label>
                                <div className="mt-2 flex flex-wrap gap-2">
                                    {MOVE_DATES.map((m) => (
                                        <button
                                            key={m}
                                            type="button"
                                            data-testid={`move-${m.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                            onClick={() => update("move_date", m)}
                                            className={`px-3 py-2 rounded-full text-sm border ${state.move_date === m ? "bg-emerald-500/15 border-emerald-500 text-emerald-200" : "border-white/10 text-slate-300"}`}
                                        >
                                            {m}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {step === 3 && (
                    <div data-testid="step-usage">
                        <h3 className="text-xl font-semibold text-white">About monthly electricity usage</h3>
                        <p className="text-sm text-slate-400 mt-1">An estimate is fine. Pick "I don't know" if you're unsure.</p>
                        <div className="mt-5 grid gap-3 sm:grid-cols-2">
                            {USAGE_BUCKETS.map((u) => (
                                <button
                                    key={u}
                                    type="button"
                                    data-testid={`usage-${u.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 30)}`}
                                    onClick={() => update("monthly_usage", u)}
                                    className={`option-tile text-left ${state.monthly_usage === u ? "selected" : ""}`}
                                >
                                    {u}
                                </button>
                            ))}
                        </div>
                        {isBusiness && (
                            <div className="mt-5 grid gap-3 sm:grid-cols-2">
                                <div>
                                    <label className="text-xs text-slate-400">Business type</label>
                                    <select
                                        data-testid="business-type-select"
                                        value={state.business_type}
                                        onChange={(e) => update("business_type", e.target.value)}
                                        className="input-dark mt-1"
                                    >
                                        <option value="">Select business type</option>
                                        {BUSINESS_TYPES.map((b) => <option key={b} value={b}>{b}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs text-slate-400">Number of locations</label>
                                    <input
                                        data-testid="locations-input"
                                        inputMode="numeric"
                                        value={state.number_of_locations}
                                        onChange={(e) => update("number_of_locations", e.target.value.replace(/[^0-9]/g, ""))}
                                        className="input-dark mt-1"
                                        placeholder="1"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {step === 4 && (
                    <div data-testid="step-contact">
                        <h3 className="text-xl font-semibold text-white">How should we reach you?</h3>
                        <p className="text-sm text-slate-400 mt-1">A real person will reach out. No bots.</p>
                        <div className="mt-5 grid gap-3 sm:grid-cols-2">
                            <div>
                                <label className="text-xs text-slate-400">First name</label>
                                <input data-testid="first-name-input" value={state.first_name} onChange={(e) => update("first_name", e.target.value)} className="input-dark mt-1" />
                            </div>
                            <div>
                                <label className="text-xs text-slate-400">Last name</label>
                                <input data-testid="last-name-input" value={state.last_name} onChange={(e) => update("last_name", e.target.value)} className="input-dark mt-1" />
                            </div>
                            <div>
                                <label className="text-xs text-slate-400">Phone</label>
                                <input data-testid="phone-input" value={state.phone} onChange={(e) => update("phone", e.target.value)} inputMode="tel" className="input-dark mt-1" placeholder="(713) 555-0123" />
                            </div>
                            <div>
                                <label className="text-xs text-slate-400">Email</label>
                                <input data-testid="email-input" value={state.email} onChange={(e) => update("email", e.target.value)} inputMode="email" className="input-dark mt-1" placeholder="you@example.com" />
                            </div>
                        </div>
                        <div className="mt-5">
                            <label className="text-xs text-slate-400">Preferred contact method</label>
                            <div className="mt-2 grid gap-2 sm:grid-cols-3">
                                {CONTACT_METHODS.map((c) => (
                                    <button
                                        type="button"
                                        key={c.id}
                                        data-testid={`contact-${c.id}`}
                                        onClick={() => update("preferred_contact", c.id)}
                                        className={`option-tile justify-center ${state.preferred_contact === c.id ? "selected" : ""}`}
                                    >
                                        {c.id === "phone" && <Phone className="w-4 h-4" />}
                                        {c.id === "text" && <MessageSquare className="w-4 h-4" />}
                                        {c.id === "email" && <Mail className="w-4 h-4" />}
                                        <span className="text-sm">{c.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="mt-5">
                            <label className="text-xs text-slate-400">Anything we should know? (optional)</label>
                            <textarea
                                data-testid="notes-input"
                                value={state.notes}
                                onChange={(e) => update("notes", e.target.value)}
                                className="input-dark mt-1 min-h-[80px]"
                                placeholder="e.g. moving in on the 15th, want fixed rate..."
                            />
                        </div>
                    </div>
                )}

                {step === 5 && (
                    <div data-testid="step-review">
                        <h3 className="text-xl font-semibold text-white">Last step - confirm & submit</h3>
                        <div className="mt-5 grid sm:grid-cols-2 gap-3 text-sm">
                            <ReviewRow label="You" value={CUSTOMER_TYPES.find((c) => c.id === state.customer_type)?.label} />
                            <ReviewRow label="Service" value={state.service_of_interest} />
                            <ReviewRow label="ZIP / State" value={`${state.zip_code} ${state.state}`} />
                            <ReviewRow label="Situation" value={state.situation} />
                            <ReviewRow label="Usage" value={state.monthly_usage} />
                            <ReviewRow label="Phone" value={state.phone} />
                            <ReviewRow label="Email" value={state.email} />
                            <ReviewRow label="Preferred contact" value={state.preferred_contact} />
                            {state.move_date && <ReviewRow label="Move date" value={state.move_date} />}
                        </div>
                        <label className="mt-6 flex items-start gap-3 cursor-pointer" data-testid="consent-row">
                            <input
                                type="checkbox"
                                data-testid="consent-checkbox"
                                checked={state.consent}
                                onChange={(e) => update("consent", e.target.checked)}
                                className="mt-1 w-5 h-5 accent-emerald-500"
                            />
                            <span className="text-sm text-slate-300 leading-relaxed">
                                I agree to be contacted by {`${state.preferred_contact || "phone, text, or email"}`} at
                                the information I provided about electricity plan options.
                                Message and data rates may apply. Consent is not a condition of purchase.
                                I can opt out any time. See our Privacy Policy.
                            </span>
                        </label>
                    </div>
                )}
            </div>

            {/* Nav */}
            <div className="mt-8 flex items-center justify-between gap-3">
                <button
                    type="button"
                    onClick={prev}
                    disabled={step === 0}
                    data-testid="form-back-btn"
                    className="btn-ghost disabled:opacity-40"
                >
                    <ChevronLeft className="w-4 h-4" /> {t("form_back")}
                </button>
                {step < stepLabels.length - 1 ? (
                    <button
                        type="button"
                        onClick={next}
                        disabled={!canNext}
                        data-testid="form-next-btn"
                        className="btn-primary disabled:opacity-50"
                    >
                        {t("form_next")} <ChevronRight className="w-4 h-4" />
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={!canNext || submitting}
                        data-testid="form-submit-btn"
                        className="btn-primary disabled:opacity-50"
                    >
                        {submitting ? t("form_submitting") : t("form_submit")}
                        {!submitting && <CheckCircle2 className="w-4 h-4" />}
                    </button>
                )}
            </div>
        </div>
    );
}

function ReviewRow({ label, value }) {
    return (
        <div className="flex items-center justify-between gap-3 rounded-xl px-3 py-2 border border-white/5 bg-white/[0.02]">
            <span className="text-slate-400">{label}</span>
            <span className="text-white font-medium truncate ml-3">{value || "—"}</span>
        </div>
    );
}
